import { FunctionComponent, useContext, useRef, useState } from 'react'
import { kuiperClassNames, Loader } from '@opallabs/kuiper'
import { toast } from '@opallabs/lapidary'
import { ContentCreatedToast } from '../../../../../Toasts'
import { useCreateContent } from '../../../../queries/useCreateContent'
import { ServiceOption } from '../../../../queries/useSystemServices'
import { WeekViewGlobalContext } from '../../../../weekViewGlobalContext'
import { Moment } from '../../momentCardTypes'
import { AddContentPopover } from '../AddContentPopover'
import './styles.scss'

type ContentSummaryProps = {
  moment: Moment
}

const ContentSummary: FunctionComponent<ContentSummaryProps> = ({ moment }) => {
  const [showAddButton, setShowAddButton] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const { setDisableActiveColumn, loadUpdatedJsonApiEntities, refetchMoments } =
    useContext(WeekViewGlobalContext)
  const summaryRef = useRef<HTMLDivElement>(null)

  const createContent = useCreateContent()
  const onCreateContent = async (
    selectedServices: ServiceOption[],
    byKeypress: boolean
  ) => {
    // `mutateAsync`, not `mutate`: a per-call `onSuccess` fires only for the
    // latest call, so with several channels the earlier creates would be lost.
    const results = await Promise.allSettled(
      selectedServices.map((service) =>
        createContent
          .mutateAsync({ serviceId: service.id, momentId: moment.id })
          .then((data) => {
            toast(
              (props) => (
                <ContentCreatedToast
                  {...props}
                  selectedServices={selectedServices}
                />
              ),
              {
                type: 'success',
                toastId: 'success-content-added-to-moment',
                closeOnClick: true,
              }
            )

            /** Refocus the content summary as popover closes */
            if (byKeypress) {
              summaryRef.current?.focus()
            } else {
              setShowAddButton(false)
            }

            /** Deserialize content response into redux. */
            if (typeof loadUpdatedJsonApiEntities === 'function') {
              loadUpdatedJsonApiEntities(data)
            }
          })
      )
    )
    // Creating content leaves the moment's `updated_at` unchanged, so
    // `loadUpdatedJsonApiEntities` keeps the stored moment and its old content
    // list. Only a refetch brings in the new content (#16447).
    if (results.some((result) => result.status === 'fulfilled')) {
      refetchMoments()
    }
  }

  const onOpenPopover = () => {
    setDisableActiveColumn(true)
    setPopoverOpen(true)
  }
  const onClosePopover = () => {
    setPopoverOpen(false)
    setDisableActiveColumn(false)
  }

  /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
  return (
    <div
      className={kuiperClassNames('contentList--summary')}
      ref={summaryRef}
      onFocus={() => setShowAddButton(true)}
      onMouseEnter={() => !popoverOpen && setShowAddButton(true)}
      onMouseLeave={() => !popoverOpen && setShowAddButton(false)}
      tabIndex={0}
    >
      {createContent.isLoading && (
        <div className={kuiperClassNames('loader-wrapper')}>
          <Loader loaded={false} theme="transparent" dotSize="small" />
        </div>
      )}
      {showAddButton && !createContent.isLoading && (
        <AddContentPopover
          buttonIsOutlined={false}
          onCreateContent={onCreateContent}
          onClosePopover={onClosePopover}
          onOpenPopover={onOpenPopover}
          onBlurButton={() => !popoverOpen && setShowAddButton(false)}
        />
      )}
      {!showAddButton && !createContent.isLoading && (
        <span className={kuiperClassNames('summary-text')}>Content</span>
      )}
    </div>
  )
  /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
}

export default ContentSummary
